import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Lets a screen put its own name — and, optionally, an "up one level" action —
 * into the app bar on small viewports.
 *
 * The mobile references drop each screen's `<h1>` from the page body and carry
 * it in the app bar instead, next to the hamburger. Rather than teach `TopNav`
 * about routes, screens register what they want shown; anything that registers
 * nothing leaves the bar exactly as it was, so this is inert for every screen
 * that has not been converted yet.
 */
export interface PageHeaderState {
  /** Screen (or current folder) name shown beside the menu button. */
  title?: ReactNode;
  /**
   * When set, the app bar swaps the hamburger for a back arrow. Screens that
   * drill into their own sub-levels (folders, wizards) use this so the user
   * is not left with only a horizontally-scrolling breadcrumb.
   */
  onBack?: () => void;
  /**
   * Trailing action(s) for the app bar. The small-viewport references drop a
   * screen's header buttons along with its `<h1>` and carry the one that
   * matters up here — Contacts' "Add", say. Rendered ahead of the bar's own
   * icon group, so a screen that registers nothing leaves the bar untouched.
   */
  actions?: ReactNode;
}

interface PageHeaderContextValue {
  header: PageHeaderState;
  setHeader: (next: PageHeaderState) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue>({
  header: {},
  setHeader: () => {},
});

export const PageHeaderProvider = (props: { children: ReactNode }) => {
  const [header, setHeader] = useState<PageHeaderState>({});
  const value = useMemo(() => ({ header, setHeader }), [header]);
  return (
    <PageHeaderContext.Provider value={value}>
      {props.children}
    </PageHeaderContext.Provider>
  );
};

/** Read side — used by `TopNav`. */
export const usePageHeader = () => useContext(PageHeaderContext).header;

/**
 * Write side — used by a screen. The callback is held in a ref so a new inline
 * function on every render does not re-register (and re-render the app bar)
 * each time; only the title and whether a back action exists are tracked.
 *
 * `title` and `actions` are compared by identity, so a caller passing an
 * element has to memoise it — a fresh node every render would re-register the
 * header on every render.
 */
export const useSetPageHeader = (
  title?: ReactNode,
  onBack?: () => void,
  actions?: ReactNode,
) => {
  const { setHeader } = useContext(PageHeaderContext);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const hasBack = !!onBack;

  const stableBack = useCallback(() => onBackRef.current?.(), []);

  useEffect(() => {
    setHeader({ title, onBack: hasBack ? stableBack : undefined, actions });
    return () => setHeader({});
  }, [title, hasBack, stableBack, actions, setHeader]);
};

export default PageHeaderContext;
